// Locale persists to BOTH localStorage and database (cross-device sync).
// See use-preferences.ts for the sync mechanism.
import { create } from 'zustand';
import { syncLocaleToServer } from '../hooks/use-preferences';

type Locale = 'en' | 'sv';

interface I18nState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const useI18n = create<I18nState>((set) => ({
  locale:
    (typeof window !== 'undefined' && (localStorage.getItem('eligaro-locale') as Locale)) || 'en',
  setLocale: (locale) => {
    localStorage.setItem('eligaro-locale', locale);
    syncLocaleToServer(locale);
    set({ locale });
  }
}));

const translations: Record<Locale, Record<string, string>> = {
  en: {
    'nav.dashboard': 'Dashboard',
    'nav.profile': 'Profile',
    'nav.matches': 'Matches',
    'nav.team': 'Team',
    'nav.assignments': 'Assignments',
    'nav.myAssignments': 'My Assignments',
    'nav.matrix': 'Matrix',
    'nav.settings': 'Settings',
    'nav.admin': 'Admin',
    'nav.sales': 'Sales',
    'nav.marketInsights': 'Market Insights',
    'nav.notifications': 'Notifications',
    'nav.home': 'Home',
    'nav.more': 'More',
    'dashboard.title': 'Dashboard',
    'dashboard.activeAssignments': 'Active Assignments',
    'dashboard.newMatches': 'New Matches',
    'dashboard.teamCoverage': 'Team Coverage',
    'dashboard.topMatches': 'Top Matches',
    'dashboard.runMatching': 'Run Matching',
    'dashboard.running': 'Running...',
    'dashboard.viewAll': 'View all matches',
    'matches.title': 'Matches',
    'matches.search': 'Search candidates or assignments...',
    'matches.minScore': 'Min Score:',
    'matches.generatePitch': 'Generate Pitch',
    'matches.generating': 'Generating...',
    'matches.pushToAts': 'Push to ATS',
    'matches.matchDetails': 'Match Details',
    'matches.whyMatches': 'Why This Matches',
    'matches.skillsMatch': 'Skills Match',
    'matches.skillsGap': 'Skills Gap',
    'matches.recommendations': 'Recommendations',
    'assignments.title': 'Assignments',
    'assignments.matchMe': 'Match Me',
    'assignments.matching': 'Matching...',
    'assignments.save': 'Save',
    'assignments.description': 'Description',
    'assignments.skills': 'Skills',
    'assignments.contact': 'Contact',
    'assignments.filterLocation': 'Filter by location...',
    'assignments.skillsMatch': 'skills match',
    'assignments.loading': 'Loading assignments...',
    'assignments.empty': 'No assignments found.',
    'myAssignments.title': 'My Assignments',
    'myAssignments.loading': 'Loading...',
    'myAssignments.empty': 'No saved assignments',
    'myAssignments.emptyHint':
      'Save assignments from the Assignments page to track your application status here.',
    'myAssignments.markApplied': 'Mark Applied',
    'myAssignments.won': 'Won',
    'myAssignments.lost': 'Lost',
    'profile.title': 'My Profile',
    'profile.uploadCv': 'Upload CV',
    'profile.uploadFile': 'Upload File',
    'profile.pasteText': 'Paste Text',
    'profile.pastePlaceholder': 'Paste your CV text here...',
    'profile.parseCvText': 'Parse CV Text',
    'profile.dropHere': 'Drop your CV here',
    'profile.pdfOrWord': 'PDF or Word format, max 10MB',
    'profile.parsing': 'Parsing CV...',
    'profile.uploaded': 'CV uploaded successfully',
    'profile.skills': 'Skills',
    'profile.workExperience': 'Work Experience',
    'profile.education': 'Education',
    'profile.certifications': 'Certifications',
    'profile.loading': 'Loading profile...',
    'profile.welcome': 'Welcome to Eligaro',
    'profile.welcomeHint':
      'Upload your CV to create your profile. The AI will extract your skills, experience, and qualifications.',
    'team.title': 'Team Analysis',
    'team.refresh': 'Refresh Team Profile',
    'team.refreshing': 'Refreshing...',
    'team.members': 'Team Members',
    'team.totalSkills': 'Total Skills',
    'team.skillGaps': 'Skill Gaps',
    'settings.title': 'Settings',
    'settings.language': 'Language',
    'admin.title': 'Admin',
    'admin.syncAssignments': 'Sync Assignments',
    'admin.syncing': 'Syncing...',
    'admin.systemHealth': 'System Health',
    'admin.activityLog': 'Activity Log',
    'admin.loadingHealth': 'Loading health checks...',
    'admin.loadingLogs': 'Loading logs...',
    'admin.noLogs': 'No activity logged yet.',
    'matrix.title': 'Competence Matrix',
    'matrix.searchPlaceholder': 'Search by name or skill...',
    'matrix.loading': 'Loading matrix...',
    'matrix.error': 'Failed to load competence matrix. Please try again.',
    'matrix.empty': 'No team members found.',
    'matrix.teamMembers': 'team members',
    'matches.loading': 'Loading matches...',
    'matches.empty': 'No matches found. Try adjusting filters or run matching.',
    'common.loading': 'Loading...',
    'common.noData': 'No data found.',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.refresh': 'Refresh'
  },
  sv: {
    'nav.dashboard': 'Översikt',
    'nav.profile': 'Profil',
    'nav.matches': 'Matchningar',
    'nav.team': 'Team',
    'nav.assignments': 'Uppdrag',
    'nav.myAssignments': 'Mina Uppdrag',
    'nav.matrix': 'Matris',
    'nav.settings': 'Inställningar',
    'nav.admin': 'Admin',
    'nav.sales': 'Försäljning',
    'nav.marketInsights': 'Marknadsinblick',
    'nav.notifications': 'Notiser',
    'nav.home': 'Hem',
    'nav.more': 'Mer',
    'dashboard.title': 'Översikt',
    'dashboard.activeAssignments': 'Aktiva uppdrag',
    'dashboard.newMatches': 'Nya matchningar',
    'dashboard.teamCoverage': 'Teamtäckning',
    'dashboard.topMatches': 'Topp-matchningar',
    'dashboard.runMatching': 'Kör matchning',
    'dashboard.running': 'Kör...',
    'dashboard.viewAll': 'Visa alla matchningar',
    'matches.title': 'Matchningar',
    'matches.search': 'Sök kandidater eller uppdrag...',
    'matches.minScore': 'Min poäng:',
    'matches.generatePitch': 'Generera pitch',
    'matches.generating': 'Genererar...',
    'matches.pushToAts': 'Skicka till ATS',
    'matches.matchDetails': 'Matchdetaljer',
    'matches.whyMatches': 'Varför detta matchar',
    'matches.skillsMatch': 'Matchade kompetenser',
    'matches.skillsGap': 'Kompetensgap',
    'matches.recommendations': 'Rekommendationer',
    'assignments.title': 'Uppdrag',
    'assignments.matchMe': 'Matcha mig',
    'assignments.matching': 'Matchar...',
    'assignments.save': 'Spara',
    'assignments.description': 'Beskrivning',
    'assignments.skills': 'Kompetenser',
    'assignments.contact': 'Kontakt',
    'assignments.filterLocation': 'Filtrera efter plats...',
    'assignments.skillsMatch': 'kompetenser matchar',
    'assignments.loading': 'Laddar uppdrag...',
    'assignments.empty': 'Inga uppdrag hittades.',
    'myAssignments.title': 'Mina Uppdrag',
    'myAssignments.loading': 'Laddar...',
    'myAssignments.empty': 'Inga sparade uppdrag',
    'myAssignments.emptyHint':
      'Spara uppdrag från sidan Uppdrag för att följa din ansökningsstatus här.',
    'myAssignments.markApplied': 'Markera som sökt',
    'myAssignments.won': 'Vunnen',
    'myAssignments.lost': 'Förlorad',
    'profile.title': 'Min profil',
    'profile.uploadCv': 'Ladda upp CV',
    'profile.uploadFile': 'Ladda upp fil',
    'profile.pasteText': 'Klistra in text',
    'profile.pastePlaceholder': 'Klistra in din CV-text här...',
    'profile.parseCvText': 'Analysera CV-text',
    'profile.dropHere': 'Släpp ditt CV här',
    'profile.pdfOrWord': 'PDF- eller Word-format, max 10MB',
    'profile.parsing': 'Analyserar CV...',
    'profile.uploaded': 'CV uppladdat',
    'profile.skills': 'Kompetenser',
    'profile.workExperience': 'Arbetslivserfarenhet',
    'profile.education': 'Utbildning',
    'profile.certifications': 'Certifieringar',
    'profile.loading': 'Laddar profil...',
    'profile.welcome': 'Välkommen till Eligaro',
    'profile.welcomeHint':
      'Ladda upp ditt CV för att skapa din profil. AI:n extraherar dina kompetenser, erfarenheter och kvalifikationer.',
    'team.title': 'Teamanalys',
    'team.refresh': 'Uppdatera teamprofil',
    'team.refreshing': 'Uppdaterar...',
    'team.members': 'Teammedlemmar',
    'team.totalSkills': 'Totalt antal kompetenser',
    'team.skillGaps': 'Kompetensgap',
    'settings.title': 'Inställningar',
    'settings.language': 'Språk',
    'admin.title': 'Admin',
    'admin.syncAssignments': 'Synka uppdrag',
    'admin.syncing': 'Synkar...',
    'admin.systemHealth': 'Systemhälsa',
    'admin.activityLog': 'Aktivitetslogg',
    'admin.loadingHealth': 'Laddar hälsokontroller...',
    'admin.loadingLogs': 'Laddar loggar...',
    'admin.noLogs': 'Ingen aktivitet loggad ännu.',
    'matrix.title': 'Kompetenmatris',
    'matrix.searchPlaceholder': 'Sök på namn eller kompetens...',
    'matrix.loading': 'Laddar matris...',
    'matrix.error': 'Det gick inte att ladda kompetensmatrisen. Försök igen.',
    'matrix.empty': 'Inga teammedlemmar hittades.',
    'matrix.teamMembers': 'teammedlemmar',
    'matches.loading': 'Laddar matchningar...',
    'matches.empty': 'Inga matchningar hittades. Försök justera filter eller kör matchning.',
    'common.loading': 'Laddar...',
    'common.noData': 'Ingen data hittades.',
    'common.save': 'Spara',
    'common.cancel': 'Avbryt',
    'common.delete': 'Ta bort',
    'common.refresh': 'Uppdatera'
  }
};

export function t(key: string): string {
  const locale = useI18n.getState().locale;
  return translations[locale][key] ?? key;
}

export function useT() {
  const locale = useI18n((s) => s.locale);
  return (key: string) => translations[locale][key] ?? key;
}
